import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Chip, Button, TextField } from '@mui/material';
import { LibraryBooks } from '@mui/icons-material';
import ImportExportBar from '../../components/ImportExport/ImportExportBar';
import { onDataUpdated } from '../../utils/dataBus';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

interface Ligne {
  numero_compte: string;
  libelle: string;
  debit: number;
  credit: number;
}

interface Ecriture {
  id: string;
  date_ecriture: string;
  journal: string;
  libelle: string;
  montant_total: number;
  statut: 'brouillard' | 'valide' | 'annule';
  lignes: Ligne[];
}

const STORAGE_KEY = 'comptaEBNL_ecritures_local';

const GrandLivre: React.FC = () => {
  const [ecritures, setEcritures] = useState<Ecriture[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [accountFilter, setAccountFilter] = useState<string>('');

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try { setEcritures(JSON.parse(raw)); } catch { setEcritures([]); }
    }
    const off = onDataUpdated(() => {
      const r = localStorage.getItem(STORAGE_KEY);
      if (r) { try { setEcritures(JSON.parse(r)); } catch {} }
    });
    return off;
  }, []);

  const tokens = useMemo(() => accountFilter.split(/[,;\s]+/).map(t => t.trim()).filter(Boolean), [accountFilter]);

  const comptes = useMemo(() => {
    const map: Record<string, { numero_compte: string; lignes: (Ligne & { date: string; journal: string; ecritureLibelle: string })[] } > = {};
    for (const e of ecritures) {
      if (fromDate && e.date_ecriture < fromDate) continue;
      if (toDate && e.date_ecriture > toDate) continue;
      for (const l of e.lignes) {
        if (!l.numero_compte) continue;
        if (tokens.length > 0 && !tokens.some(t => l.numero_compte.startsWith(t))) continue;
        if (!map[l.numero_compte]) map[l.numero_compte] = { numero_compte: l.numero_compte, lignes: [] };
        map[l.numero_compte].lignes.push({ ...l, date: e.date_ecriture, journal: e.journal, ecritureLibelle: e.libelle });
      }
    }
    Object.values(map).forEach(acc => acc.lignes.sort((a, b) => a.date.localeCompare(b.date)));
    return Object.values(map).sort((a, b) => a.numero_compte.localeCompare(b.numero_compte));
  }, [ecritures, fromDate, toDate, tokens]);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Grand livre
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Période {fromDate || '…'} — {toDate || '…'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField type="date" label="Du" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <TextField type="date" label="Au" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} />
        <TextField label="Comptes (séparés par ; , espace)" value={accountFilter} onChange={e => setAccountFilter(e.target.value)} placeholder="ex: 401; 606; 7" sx={{ minWidth: 360 }} />
        <ImportExportBar getExportCSV={() => ({ filename: 'grand_livre.csv', content: '' })} getExportJSON={() => ({ filename: 'grand_livre.json', content: JSON.stringify(comptes, null, 2) })} />
        <Button variant="contained" onClick={async () => {
          try {
            const pdfDoc = await PDFDocument.create();
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const addPage = () => pdfDoc!.addPage([595, 842]);
            let page = pdfDoc.getPages()[0] || addPage();
            const { width, height } = page.getSize();
            const draw = (t:string,x:number,y:number,s=10)=>page.drawText(t,{x,y,size:s,font,color:rgb(0,0,0)});
            draw('Grand livre', 240, height - 40, 12);
            draw(`Période ${fromDate || '…'} — ${toDate || '…'}`, 40, height - 60, 9);
            let y = height - 80;
            for (const c of comptes) {
              if (y < 120) { page = addPage(); y = height - 40; }
              draw(`Compte ${c.numero_compte}`, 40, y, 10); y -= 14;
              draw('Date', 40, y, 9); draw('Journal', 100, y, 9); draw('Libellé', 160, y, 9); draw('Débit', 420, y, 9); draw('Crédit', 480, y, 9);
              y -= 12; let cumul = 0;
              for (const l of c.lignes) {
                if (y < 60) { page = addPage(); y = height - 40; }
                draw(String(l.date), 40, y); draw(l.journal, 100, y); draw((l.libelle || l.ecritureLibelle || '').slice(0,40), 160, y);
                draw((l.debit||0).toFixed(2), 420, y); draw((l.credit||0).toFixed(2), 480, y);
                y -= 12;
              }
              y -= 8;
            }
            const bytes = await pdfDoc.save();
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'grand_livre.pdf'; a.click(); URL.revokeObjectURL(url);
          } catch {
            alert('Export PDF: échec de génération');
          }
        }}>Exporter PDF</Button>
      </Box>

      {comptes.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>Aucune écriture à afficher. Modèle de grand livre:</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Journal</TableCell>
                    <TableCell>Libellé</TableCell>
                    <TableCell align="right">Débit</TableCell>
                    <TableCell align="right">Crédit</TableCell>
                    <TableCell align="right">Solde</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} align="center">(vide)</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : (
        comptes.map((compte) => {
          let cumul = 0;
          return (
            <Card key={compte.numero_compte} sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LibraryBooks color="primary" />
                  <Typography variant="h6">Compte {compte.numero_compte}</Typography>
                </Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Journal</TableCell>
                        <TableCell>Libellé</TableCell>
                        <TableCell align="right">Débit</TableCell>
                        <TableCell align="right">Crédit</TableCell>
                        <TableCell align="right">Solde</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {compte.lignes.map((l, idx) => {
                        cumul += (Number(l.debit) || 0) - (Number(l.credit) || 0);
                        return (
                          <TableRow key={idx} hover>
                            <TableCell>{l.date}</TableCell>
                            <TableCell>{l.journal}</TableCell>
                            <TableCell>{l.libelle || l.ecritureLibelle}</TableCell>
                            <TableCell align="right">{(l.debit || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{(l.credit || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{cumul.toFixed(2)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          );
        })
      )}
    </Box>
  );
};

export default GrandLivre;